import pitch from './brand/icons/pitch.svg?raw';
import flag from './brand/icons/flag.svg?raw';
import report from './brand/icons/report.svg?raw';
import setPiece from './brand/icons/set-piece.svg?raw';
import opponentFile from './brand/icons/opponent-file.svg?raw';
import roster from './brand/icons/roster.svg?raw';
import archive from './brand/icons/archive.svg?raw';
import voiceNote from './brand/icons/voice-note.svg?raw';
import whistle from './brand/icons/whistle.svg?raw';

/**
 * B-5 — the one `<Icon name>` wrapper the brand kit's destination table asks for
 * (`docs/design/b-brand/README.md`: *"import with Vite's `?raw` and inline, or wrap in one
 * `<Icon name>` component"*). Inlining is what makes the icons themable: the masters carry
 * hardcoded slate ink (`#20242E` strokes, and slate fills on solid shapes), so an `<img>` could
 * never follow the active-tab accent or the dark theme. `styles.css` recolors the inlined
 * markup to `currentColor` — strokes unconditionally, fills **only where the fill is the slate
 * ink** — so deliberate non-ink fills (the whistle's white mouthpiece occlusion, per the kit
 * README) survive recoloring when later units add those icons here.
 *
 * The map holds exactly the icons B-5 renders; later units append (the kit ships 28). A name is
 * a compile-time union, so a typo is a build error rather than an empty box at a pitch.
 */

const ICONS = {
  /** Matches tab (§2.4). */
  pitch,
  /** Opponents tab. */
  'opponent-file': opponentFile,
  /** My Team tab. */
  roster,
  /** More tab. */
  archive,
  /** B-7 — the setup microphone card. */
  'voice-note': voiceNote,
  /** B-8 — the takeover's guarded transition control. */
  whistle,
  /** B-11 — the bottom band's Flag button. */
  flag,
  /** B-12 — the bottom band's set-piece flank button. */
  'set-piece': setPiece,
  /** B-13 — the bottom band's flags-review flank button (distinct from the flag press itself). */
  report,
} as const;

export type IconName = keyof typeof ICONS;

/**
 * Decorative by default: everywhere B-5 uses an icon its meaning is carried by an adjacent
 * text label (tab labels are always shown, §2.4), so the markup is `aria-hidden` and the label
 * does the talking. 24×24 artboard at stroke 1.75 per the kit; size overrides are CSS.
 */
export function Icon({ name }: { readonly name: IconName }) {
  return (
    <span className="icon-b" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICONS[name] }} />
  );
}
